/** The Cache tab (plan §1.1, §3): what the booking's caches hold and the actions that
 *  rebuild them — the same actions as the Actions menu's Cache group (one owner,
 *  `useBookingCacheActions`), each section showing the last result whichever started it.
 *
 *  - **Mobile app cache** — the version-cache records the app serves the booking from
 *    (`GET …/vcache/`, which replaced the old cache_records list) with the publish verdict
 *    computed now, and Refresh… (it shows that verdict before it runs: a refresh of a
 *    booking the app must not show tombstones its entry).
 *  - **Guide check-in list** — whether the booking is in its departure's list and when the
 *    window opens; Rebuild.
 *  - **Notification context** — the tag-context cache notification rendering reads;
 *    Rebuild.
 *  - **Chat membership** — Resync… (queued; Twilio).
 *
 *  Times in the tour's zone, labelled. */
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Alert, Box, Button, Link, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import type { ReactNode } from 'react';

import { useBookingVcache, type BookingVcacheEntry } from '@/api/bookingActions';
import type { BookingRecordEditContext } from '@/api/bookingRecord';
import { Field } from '@/components/ReadView';
import { formatZoned } from '@/utils/zonedTime';

import { checkinWindowText, inManifestText, MONO_SX } from '../panels/displays';
import {
  chatText,
  manifestText,
  tagContextText,
  vcacheText,
  type BookingCacheActions,
  type CacheActionControl,
  type Ran,
} from '../useBookingCacheActions';
import { PublishVerdict, RefreshEffect } from '../VcacheVerdict';

import { TabSection } from './TabSection';
import { TabSkeleton } from './TabSkeleton';

export const VCACHE_SECTION_ID = 'booking-cache-vcache';

function bytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

const RECORD_LABEL: Record<string, string> = {
  tour_participant: 'The booking',
  tour_package: 'Its legacy package',
  tour_template: 'Its tour’s template',
  tour: 'Its itinerary',
  user: 'The guest’s profile',
};

/** The action button of a section: absent without the permission, disabled with the
 *  reason, busy while it runs. */
function ActionButton<R>({ control, label }: { control: CacheActionControl<R> | null; label: string }) {
  if (!control) return null;
  const button = (
    <Button
      size="small"
      variant="outlined"
      disabled={Boolean(control.disabledReason) || control.pending}
      onClick={(e) => control.start(e.currentTarget)}
    >
      {control.pending ? 'Running…' : label}
    </Button>
  );
  return control.disabledReason ? (
    <Tooltip title={control.disabledReason}>
      <span>{button}</span>
    </Tooltip>
  ) : (
    button
  );
}

function LastRun<R>({ last, tz, text }: { last: Ran<R> | null; tz: string; text: (r: R) => string }) {
  if (!last) return null;
  return (
    <Typography variant="body2" component="p" role="status" sx={{ m: 0, display: 'flex', gap: 0.75 }}>
      <CheckCircleOutlineIcon aria-hidden sx={{ fontSize: 16, mt: '2px', color: 'success.main', flex: 'none' }} />
      <span>
        {formatZoned(last.at, tz, { weekday: false })}: {text(last.value)}
      </span>
    </Typography>
  );
}

function Note({ children }: { children: ReactNode }) {
  return (
    <Typography variant="body2" color="text.secondary" component="p" sx={{ m: 0 }}>
      {children}
    </Typography>
  );
}

function Entries({ entries, tz }: { entries: BookingVcacheEntry[]; tz: string }) {
  if (entries.length === 0) return <Note>No mobile cache records for this booking.</Note>;
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small" aria-label="Mobile cache records" sx={{ '& td, & th': { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' } }}>
        <TableHead>
          <TableRow>
            <TableCell>Record</TableCell>
            <TableCell>State</TableCell>
            <TableCell>Spec</TableCell>
            <TableCell align="right">Version</TableCell>
            <TableCell>Updated</TableCell>
            <TableCell align="right">Size</TableCell>
            <TableCell>Redis</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {entries.map((e) => (
            <TableRow key={e.id} data-vcache-entry={e.api_type ?? ''}>
              <TableCell>
                <Box component="span" sx={{ display: 'block' }}>
                  {RECORD_LABEL[e.api_type ?? ''] ?? e.api_type ?? 'Record'}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={MONO_SX}>
                  {e.api_type ?? '—'} #{e.object_id ?? '—'}
                  {e.shared ? ' · shared' : ''}
                </Typography>
              </TableCell>
              <TableCell>
                {e.is_deleted ? (
                  <Box component="span" sx={{ display: 'inline-flex', gap: 0.5, alignItems: 'center' }}>
                    <DeleteOutlineIcon aria-hidden sx={{ fontSize: 16, color: 'warning.main' }} />
                    Tombstone
                  </Box>
                ) : (
                  'Live'
                )}
              </TableCell>
              <TableCell sx={MONO_SX}>{e.spec_version}</TableCell>
              <TableCell align="right" sx={MONO_SX}>
                {e.version ?? '—'}
              </TableCell>
              <TableCell>{e.timestamp ? formatZoned(e.timestamp, tz, { zone: false }) : '—'}</TableCell>
              <TableCell align="right">
                {bytes(e.size)}
                {e.url && (
                  <>
                    {' · '}
                    <Link href={e.url} target="_blank" rel="noopener noreferrer" underline="hover">
                      CDN
                    </Link>
                  </>
                )}
              </TableCell>
              <TableCell>
                {e.redis.data_bytes > 0 ? (
                  <Tooltip title={e.redis.data_key ?? ''}>
                    <span>{bytes(e.redis.data_bytes)}</span>
                  </Tooltip>
                ) : (
                  <Typography variant="body2" component="span" color="text.secondary">
                    absent
                  </Typography>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

function Vcache({ bookingId, tz, cache }: { bookingId: number; tz: string; cache: BookingCacheActions }) {
  const q = useBookingVcache(bookingId);
  const v = q.data;
  return (
    <TabSection
      id={VCACHE_SECTION_ID}
      title="Mobile app cache"
      action={<ActionButton control={cache.vcacheRefresh} label="Refresh…" />}
    >
      {q.isLoading ? (
        <TabSkeleton label="Loading the mobile cache" />
      ) : q.error || !v ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => void q.refetch()}>
              Retry
            </Button>
          }
        >
          Couldn’t load the mobile cache.
        </Alert>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 1.5 }} data-published={String(v.publish.is_published)}>
          <PublishVerdict publish={v.publish} />
          <RefreshEffect publish={v.publish} />
          <LastRun last={cache.vcacheRefresh?.last ?? null} tz={tz} text={vcacheText} />
          <Entries entries={v.entries} tz={tz} />
          <Typography variant="caption" color="text.secondary">
            Read {formatZoned(v.generated_ts, tz, { weekday: false })} · spec versions {v.spec_versions.join(', ') || '—'} ·
            shared records (the tour template, the guest’s profile) are not rewritten by this booking’s refresh
          </Typography>
        </Box>
      )}
    </TabSection>
  );
}

export function CacheTab({
  bookingId,
  editContext,
  cache,
}: {
  bookingId: number;
  editContext: BookingRecordEditContext;
  cache: BookingCacheActions;
}) {
  const tz = editContext.schedule.timezone;
  const { checkin, schedule } = editContext;
  const grid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(176px, 1fr))', columnGap: 2 } as const;

  return (
    <Box>
      <Vcache bookingId={bookingId} tz={tz} cache={cache} />

      <TabSection id="booking-cache-checkin" title="Guide check-in list" action={<ActionButton control={cache.manifest} label="Rebuild" />}>
        <Box sx={{ display: 'grid', gap: 1 }}>
          <Box sx={grid}>
            <Field label="Departure" value={schedule.start_ts ? formatZoned(schedule.start_ts, tz) : null} />
            <Field label="In the guide’s check-in list" value={inManifestText(checkin)} />
            <Field label="Check-in window" value={checkinWindowText(checkin, tz)} />
          </Box>
          <Note>The list exists only while the tenant’s check-in window is open; a rebuild re-renders it now.</Note>
          <LastRun last={cache.manifest?.last ?? null} tz={tz} text={manifestText} />
        </Box>
      </TabSection>

      <TabSection
        id="booking-cache-tag-context"
        title="Notification context"
        action={<ActionButton control={cache.tagContext} label="Rebuild" />}
      >
        <Box sx={{ display: 'grid', gap: 1 }}>
          <Note>
            The booking’s tags and tag_context as notification rendering reads them, cached in Redis until the trip is
            long over.
          </Note>
          <LastRun last={cache.tagContext?.last ?? null} tz={tz} text={tagContextText} />
        </Box>
      </TabSection>

      <TabSection id="booking-cache-chat" title="Chat membership" action={<ActionButton control={cache.chat} label="Resync…" />}>
        <Box sx={{ display: 'grid', gap: 1 }}>
          <Note>
            A resync queues one full chat sync of the tour’s channel and threads on the travel date — in the background,
            Twilio call by call.
          </Note>
          <LastRun last={cache.chat?.last ?? null} tz={tz} text={chatText} />
        </Box>
      </TabSection>
    </Box>
  );
}
